import { useCallback, useEffect, useRef } from 'react';

export const TRACKING_ATTRIBUTE_NAME = 'data-track-element';
export const TRACKING_PAYLOAD_ATTRIBUTE_NAME = 'data-track-event-payload';

export const useTrackElementVisibleOnce = (eventsMaps: Record<string, (payload: string) => void>) => {
	const observer = useRef<Nullable<IntersectionObserver>>(null);
	const observedElements = useRef(new WeakSet<HTMLElement>());

	useEffect(() => {
		observer.current = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const target = entry.target as HTMLElement;
						const payload = target.getAttribute(TRACKING_PAYLOAD_ATTRIBUTE_NAME);
						const taggedChildren = target.querySelectorAll<HTMLElement>(`[${TRACKING_ATTRIBUTE_NAME}]`);
						const tags = Array.from(taggedChildren).map((el) => el?.getAttribute(TRACKING_ATTRIBUTE_NAME));

						if (payload && tags.length) {
							tags.forEach((tag) => {
								if (tag && tag in eventsMaps) {
									eventsMaps[tag](payload);
								}
							});
						}

						observer.current?.unobserve(target);
					}
				});
			},
			{
				threshold: 1,
			},
		);

		return () => {
			observer.current?.disconnect();
		};
	}, [eventsMaps]);

	const setTrackElementRef = useCallback((el: Nullable<HTMLElement>) => {
		if (!el) return;

		if (observer.current && !observedElements.current.has(el)) {
			observer.current.observe(el);
			observedElements.current.add(el);
		}
	}, []);

	return { setTrackElementRef };
};
